/**
 * Cognito user group name for internal Nikola team users.
 *
 * Same string used across the repo when checking access, e.g.:
 * - Main app: `cognitoUserGroup.includes("NikolaTeam")` / `UserRole.NIKOLA_TEAM`
 *   (`apps/legacy/src/contexts/AuthContext.tsx` → `isTeamMember`)
 * - `CognitoUserGroup.NIKOLA_TEAM` in `apps/legacy/src/shared/enums/CognitoUserGroup.ts`
 * - `UserGroup` in `packages/backend-shared/src/types.ts` includes `"NikolaTeam"`
 *
 * Enterprise reads this from the Cognito attribute mapped to `User.role` (`custom:role` in
 * `AuthProvider`). If your pool uses a different attribute shape, use
 * `isNikolaTeamCognitoGroup()` which also accepts comma/semicolon-separated lists.
 */
export const COGNITO_GROUP_NIKOLA_TEAM = "NikolaTeam" as const;

/**
 * True when the user belongs to the Nikola team Cognito group.
 *
 * Equivalent to `cognitoUserGroup.includes("NikolaTeam")` in the main app, but
 * accepts a single string (typical for `custom:role`) or a comma/semicolon-separated
 * list of groups.
 */
export function isNikolaTeamCognitoGroup(
  value: string | null | undefined,
): boolean {
  if (!value?.trim()) {
    return false;
  }
  const normalized = value.trim();
  if (normalized === COGNITO_GROUP_NIKOLA_TEAM) {
    return true;
  }
  return normalized
    .split(/[,;]/)
    .map((s) => s.trim())
    .some((s) => s === COGNITO_GROUP_NIKOLA_TEAM);
}

/** Same shape as `User` from `AuthProvider` for access checks. */
export type NikolaTeamAuthInput = {
  role: string;
  cognitoGroups: string[];
};

/**
 * Nikola internal access: same idea as the main app
 * `cognitoUserGroup.includes("NikolaTeam")` **or** `custom:role` containing
 * `NikolaTeam` (see `isNikolaTeamCognitoGroup`).
 */
export function isNikolaTeamMember(
  auth: NikolaTeamAuthInput | null | undefined,
): boolean {
  if (!auth) {
    return false;
  }
  if (isNikolaTeamCognitoGroup(auth.role)) {
    return true;
  }
  return auth.cognitoGroups.includes(COGNITO_GROUP_NIKOLA_TEAM);
}
